import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { getPhysicalIOLabel } from '../Utils/physical_io_label';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { isMobile } from 'react-device-detect';
import Button from '@material-ui/core/Button';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { useSnackbar } from 'notistack';
import { useSnapshot } from 'valtio';

import { SettingActions } from '../components/index.js';
import { settingControls, useSavedSettings, writeTempSettings } from './settingFunctions';
import { ExpandableSettingsGroup, ValueBox } from '../FormWidgets/SettingGroup';
import { LoadSettingsDialog } from '../Utils/load_settings';

import getStateManager from '../StateManager.js';
import {
  MultiIOSelect,
  MultiIOSelectIndex,
  OverwrIOSelectType,
  OverwrIOSelectIO,
  OverwrIOFuncSelect
} from '../FormWidgets/Select.js';
import { OverwrIOSelect, OverwrIOTimeout } from '../FormWidgets/OverwrIOControl.js';
import {
  multiIOMapping,
  multiMultiIOHwTypeMappingSelect,
  multiIOHwTypeMappping,
  funcIndexConfig,
  getValveIrrigationRouteCount
} from '../Constants/constants.js';
import { getCirculationCount } from '../Utils/ventilation_utils';
import { makeMultiOutObject, makeOverwrIOObject } from '../Utils/utils.js';
import { areValveRouteOverridesSupported } from '../Utils/multi_io_settings';
import { ConnectionStatus } from '../FormWidgets/SlaveWidgets.js';
import { OverwrIOHeading, OverwrMultiIOHeading } from '../FormWidgets/OverwrIOLabel.js';
import AnalogOverwriteSetting from '../FormWidgets/AnalogOverwriteSetting';

import _ from 'lodash';
import {
  getNewOverwriteMode,
  getOverwriteEditorSubmissions,
  isIoOverrideConfigured,
  clearLegacyIoOverrideRowsStorage,
  buildFunctionOverwrites,
  buildIoOverwrites,
  syncFunctionOverwrites,
  recordOverwriteSubmissions,
  reconcileOverwriteSubmissions,
  applyOverwriteSubmissions
} from '../Utils/io_overwrite_settings';

const useStyles = makeStyles((theme) => ({
  box: {
    backgroundColor: theme.palette.innerContainer2.background
  },
  addButton: {
    whiteSpace: 'nowrap',
    '& .MuiButton-label': {
      whiteSpace: 'inherit'
    }
  }
}));

export default function OverwriteIOSettings(other) {
  const params = useParams();
  const { controller, parent } = getStateManager().getControllerForTab(params);
  return (
    <OverwriteIOEditor
      key={JSON.stringify([controller.id, parent.id])}
      controller={controller}
      parent={parent}
      {...other}
    />
  );
}

function OverwriteIOEditor({ controller, parent, ...other }) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [mobile, setMobile] = useState(isMobile);

  const write_access = controller.access_rights.multi_io?.write === true;

  const controllerSnapshot = useSnapshot(controller);
  const multiIOCounters = {
    ...controllerSnapshot.multi_io_counters,
    valve_irrigation_route_count: getValveIrrigationRouteCount(controllerSnapshot, parent),
    circulation_count: getCirculationCount(controllerSnapshot, parent)
  };
  let overwr_ios = makeOverwrIOObject(controllerSnapshot, parent);
  let overwr_io_sid = 'overwr_io';

  let multi_io_sid = 'multi_io';
  let multi_outs = makeMultiOutObject(controllerSnapshot, parent);
  let slaves = useSnapshot(controller.slaves);

  const [values, setValues] = useState({
    overwr_io: overwr_ios,
    multi_io: _.cloneDeep(multi_outs)
  });

  useEffect(() => {
    clearLegacyIoOverrideRowsStorage();
  }, []);
  const ioRowListed = (slave, row) =>
    !parent.temp_settings[multi_io_sid]?.[`multi_out_${slave}_${row.id}_overwr_deleted`] &&
    (isIoOverrideConfigured(row) ||
      Object.prototype.hasOwnProperty.call(
        parent.temp_settings[multi_io_sid] ?? {},
        `multi_out_${slave}_${row.id}_overwr_select`
      ));

  const controls = {
    overwr_io: settingControls(controller, parent, overwr_io_sid, values.overwr_io, (v) =>
      setValues((prev) => ({ ...prev, overwr_io: v }))
    ),
    multi_io: settingControls(controller, parent, multi_io_sid, values.multi_io, (v) =>
      setValues((prev) => ({ ...prev, multi_io: v }))
    )
  };

  const dialogs = {
    overwr_io: useSavedSettings(parent, controls.overwr_io, overwr_io_sid),
    multi_io: useSavedSettings(parent, controls.multi_io, multi_io_sid)
  };

  const {
    openLoadSettingsDialog: openOverwrIOLoadDialog,
    closeLoadSettingsDialog: closeOverwrIOLoadDialog
  } = dialogs.overwr_io;
  const {
    openLoadSettingsDialog: openMultiIOLoadDialog,
    closeLoadSettingsDialog: closeMultiIOLoadDialog
  } = dialogs.multi_io;

  const pending = getOverwriteEditorSubmissions(controller, parent);
  const [pendingClock, setPendingClock] = useState(0);
  const pendingCount = Object.keys(pending.current).length;
  useEffect(() => {
    if (!pendingCount) return undefined;
    const timer = setInterval(() => setPendingClock((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [pendingCount]);

  const readLive = () => ({
    overwr_io: controller.overwr_io,
    multi_io: makeMultiOutObject(controller, undefined)
  });
  const effectiveForSave = () => {
    const live = readLive();
    pending.current = reconcileOverwriteSubmissions(pending.current, live);
    return applyOverwriteSubmissions(live, pending.current);
  };
  const transform_sent_settings_overwr_io = (settings) =>
    buildFunctionOverwrites(
      effectiveForSave().overwr_io,
      settings,
      parent.temp_settings[overwr_io_sid],
      parent.zone_id ?? parent.id,
      funcIndexConfig
    );
  const transform_sent_settings_multi_io = (settings) =>
    buildIoOverwrites(effectiveForSave().multi_io, settings, parent.temp_settings[multi_io_sid]);

  useEffect(() => {
    if (controller.connection.awaiting_values) return;
    // Reconcile against the full controller list, then display this zone only.
    const live = {
      overwr_io: controllerSnapshot.overwr_io,
      multi_io: makeMultiOutObject(controllerSnapshot, undefined)
    };
    pending.current = reconcileOverwriteSubmissions(pending.current, live);
    const effective = applyOverwriteSubmissions(live, pending.current);
    setValues((current) => {
      const overwr_io = syncFunctionOverwrites(
        current.overwr_io,
        filterUpdatedOverwrIO(effective.overwr_io, parent),
        parent.temp_settings[overwr_io_sid]
      );
      const multi_io = buildIoOverwrites(
        filterUpdatedMulti(effective.multi_io, parent),
        current.multi_io,
        parent.temp_settings[multi_io_sid],
        false
      );
      if (overwr_io === current.overwr_io && _.isEqual(multi_io, current.multi_io)) return current;
      return { ...current, overwr_io, multi_io };
    });
  }, [
    controller,
    controllerSnapshot,
    overwr_ios,
    multi_outs,
    parent,
    pendingClock,
    multi_io_sid,
    overwr_io_sid,
    pending
  ]);

  const saveOverrides = () => {
    if (!write_access || controller.connection?.status !== 'good') return;
    const sent = {
      overwr_io: transform_sent_settings_overwr_io(values.overwr_io),
      multi_io: transform_sent_settings_multi_io(values.multi_io)
    };
    const live = readLive();
    // Check both categories before recording a submission, sending either
    // packet, or clearing drafts. Keep the established pending/readback flow.
    if (!areValveRouteOverridesSupported(
      controller, parent, sent.overwr_io, live.overwr_io, sent.multi_io, live.multi_io
    )) {
      enqueueSnackbar(t('valve_irrigation_route_unavailable'), { variant: 'error' });
      return false;
    }
    // These are independent packets. Only a successful send clears that draft
    // and becomes pending; neither result is controller confirmation.
    const functionSent = controls.overwr_io.saveSettings(() => sent.overwr_io, true);
    const ioSent = controls.multi_io.saveSettings(() => sent.multi_io, true);
    for (const [category, accepted] of [['overwr_io', functionSent], ['multi_io', ioSent]]) {
      if (accepted) pending.current = recordOverwriteSubmissions(
        { [category]: sent[category] }, { [category]: live[category] }, pending.current
      );
    }
    setValues((current) => ({
      overwr_io: functionSent ? filterUpdatedOverwrIO(sent.overwr_io, parent) : current.overwr_io,
      multi_io: ioSent ? filterUpdatedMulti(sent.multi_io, parent) : current.multi_io
    }));
    return functionSent && ioSent;
  };

  controls.overwr_io.restoreTempSettings = () => {
    let _values = { ...values.overwr_io };
    for (const [k, v] of Object.entries(parent.temp_settings[overwr_io_sid])) {
      for (let field of ['function', 'func_index', 'select', 'val_ana', 'timeout']) {
        let match = k.match(new RegExp(`overwr_io_([0-9]+)_${field}`));
        if (match) {
          let actualIdx = match[1];
          // Ensure the object exists before setting properties
          if (!_values.oios[actualIdx]) {
            _values.oios[actualIdx] = {};
          }
          _values.oios[actualIdx][field] = v;
          // If any field is undefined, delete the whole entry
          if (
            typeof _values.oios[actualIdx][field] === 'undefined' &&
            Object.values(_values.oios[actualIdx]).some((val) => typeof val === 'undefined')
          ) {
            delete _values.oios[actualIdx];
          }
        }
      }
      setValues((prev) => ({
        ...prev,
        overwr_io: _values
      }));
    }
  };

  controls.multi_io.restoreTempSettings = () => {
    let _values = { ...values.multi_io };
    for (const [k, v] of Object.entries(parent.temp_settings[multi_io_sid])) {
      for (let field of ['overwr_select', 'overwr_val_ana', 'overwr_timeout']) {
        let match = k.match(new RegExp(`multi_out_([0-9]+)_([0-9]+)_${field}`));
        if (match) {
          let slave = match[1];
          let port = match[2];
          _values[slave][port][field] = v;
        }
      }
      setValues((prev) => ({
        ...prev,
        multi_io: _values
      }));
    }
  };

  controls.overwr_io.resetSettings = () => {
    pending.current = Object.fromEntries(
      Object.entries(pending.current).filter(([, entry]) => entry.io)
    );
    setValues((prev) => ({
      ...prev,
      overwr_io: _.cloneDeep(overwr_ios)
    }));
    delete parent.temp_settings[overwr_io_sid];
    writeTempSettings(parent);
  };

  controls.multi_io.resetSettings = () => {
    pending.current = Object.fromEntries(
      Object.entries(pending.current).filter(([, entry]) => !entry.io)
    );
    setValues((prev) => ({
      ...prev,
      multi_io: _.cloneDeep(multi_outs)
    }));
    delete parent.temp_settings[multi_io_sid];
    writeTempSettings(parent);
  };

  const functionReadback = (row) => overwr_ios.oios?.find((saved) =>
    saved && ['zone', 'function', 'func_index'].every((field) =>
      String(saved[field] ?? 0) === String(row[field] ?? 0)));

  function filterUpdatedOverwrIO(updatedOverwr, parent) {
    // Filter updatedOverwr based on parent (similar to makeOverwrIOObject logic)
    let filteredOverwr = {
      functions_used_cnt: updatedOverwr.functions_used_cnt ?? 0,
      oios: []
    };

    // Filter oios by zone if parent is defined
    const filteredOios =
      parent !== undefined
        ? (updatedOverwr.oios || []).filter(
            (oio) => oio && oio.zone === (parent.zone_id ?? parent.id)
          )
        : updatedOverwr.oios || [];

    // Copy the filtered oios
    for (let i = 0; i < filteredOios.length; i++) {
      if (filteredOios[i]) {
        filteredOverwr.oios.push(filteredOios[i]);
      }
    }

    // Update the functions_used_cnt to reflect the filtered count
    filteredOverwr.functions_used_cnt = filteredOverwr.oios.length;

    return filteredOverwr;
  }

  function filterUpdatedMulti(updatedMulti, parent) {
    // Filter updatedMulti based on parent (similar to makeMultiOutObject logic)
    let filteredMulti = {};
    for (let slaveAddress in updatedMulti) {
      for (let portId in updatedMulti[slaveAddress]) {
        const mo = updatedMulti[slaveAddress][portId];
        // Filter by zone if parent is defined, otherwise include all
        if (parent !== undefined ? mo.zone === (parent.zone_id ?? parent.id) : true) {
          if (!(slaveAddress in filteredMulti)) {
            filteredMulti[slaveAddress] = {};
          }
          filteredMulti[slaveAddress][portId] = mo;
        }
      }
    }
    return filteredMulti;
  }

  // Overwr IO Handlers
  const saveFunctionOverwrFuncSelect = (actualIdx, event, value) => {
    let v = event.target.value;
    let vv = _.cloneDeep(values.overwr_io);
    vv.oios[actualIdx].function = v;
    setValues((prev) => ({ ...prev, overwr_io: vv }));
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_function`, v);
  };

  const saveFunctionOverwrFuncIndexSelect = (actualIdx, event, value) => {
    let v = event.target.value;
    let vv = _.cloneDeep(values.overwr_io);
    vv.oios[actualIdx].func_index = v;
    setValues((prev) => ({ ...prev, overwr_io: vv }));
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_func_index`, v);
  };

  const saveFunctionOverwrSelect = (actualIdx, type, event, value) => {
    const v = Number(event.target.value);
    let vv = _.cloneDeep(values.overwr_io);
    vv.oios[actualIdx].select = v;
    setValues((prev) => ({ ...prev, overwr_io: vv }));
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_select`, v);
  };

  const saveFunctionOverwrValAna = (actualIdx, event, value) => {
    let v = event.target.value;
    let vv = _.cloneDeep(values.overwr_io);
    vv.oios[actualIdx].val_ana = v;
    setValues((prev) => ({ ...prev, overwr_io: vv }));
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_val_ana`, v);
  };

  const saveFunctionOverwrTimeout = (actualIdx, event, value) => {
    let vv = _.cloneDeep(values.overwr_io);
    vv.oios[actualIdx].timeout = value.toLocaleTimeString('de-DE');
    setValues((prev) => ({ ...prev, overwr_io: vv }));
    controls.overwr_io.saveTempSettings(
      `overwr_io_${actualIdx}_timeout`,
      value.toLocaleTimeString('de-DE')
    );
  };

  const saveFunctionAddOverwrIOSelectType = (actualIdx, event, value) => {
    let v = event.target.value;
    controls.overwr_io.saveTempSettings(`overwr_io_type_add_overwr_io_${actualIdx}`, v);
  };

  const saveFunctionAddOverwrIOFuncSelect = (actualIdx, event, value) => {
    let v = event.target.value;
    controls.overwr_io.saveTempSettings(`overwr_io_func_add_overwr_io_${actualIdx}`, v);
  };

  const saveFunctionAddOverwrIOFuncIndexSelect = (actualIdx, event, value) => {
    let v = event.target.value;
    controls.overwr_io.saveTempSettings(`overwr_io_func_index_add_overwr_io_${actualIdx}`, v);
  };

  const saveFunctionAddOverwrIOSelect = (actualIdx, type, event, value) => {
    const v = Number(event.target.value);
    controls.overwr_io.saveTempSettings(`overwr_io_select_add_overwr_io_${actualIdx}`, v);
  };

  const saveFunctionAddOverwrIOValAna = (actualIdx, event, value) => {
    let v = event.target.value;
    controls.overwr_io.saveTempSettings(`overwr_io_val_ana_add_overwr_io_${actualIdx}`, v);
  };

  const saveFunctionAddOverwrIOTimeout = (actualIdx, event, value) => {
    controls.overwr_io.saveTempSettings(
      `overwr_io_timeout_add_overwr_io_${actualIdx}`,
      value.toLocaleTimeString('de-DE')
    );
  };

  const addFunctionOverwrIO = () => {
    const prefix = 'overwr_io_add_overwr_io_';
    const indices = Object.keys(parent.temp_settings[overwr_io_sid] ?? {})
      .filter((k) => k.startsWith(prefix))
      .map((k) => parseInt(k.replace(prefix, ''), 10));
    const nextIndex = indices.length > 0 ? Math.max(...indices) + 1 : 0;
    controls.overwr_io.saveTempSettings(`${prefix}${nextIndex}`, 1);

    let vv = _.cloneDeep(values.overwr_io);
    vv.functions_used_cnt = vv.functions_used_cnt + 1;
    setValues((prev) => ({ ...prev, overwr_io: vv }));
    controls.overwr_io.saveTempSettings('overwr_io_functions_used_cnt', vv.functions_used_cnt);
  };

  function canFunctionAddOverwrIO() {
    // At least one hardware type still has a free (not fully consumed) function
    return getAvailableOverwrIOTypes(-1).length > 0;
  }

  const delFunctionAddOverwrIO = (actualIdx) => {
    const keysToDelete = [
      `overwr_io_type_add_overwr_io_${actualIdx}`,
      `overwr_io_func_add_overwr_io_${actualIdx}`,
      `overwr_io_func_index_add_overwr_io_${actualIdx}`,
      `overwr_io_select_add_overwr_io_${actualIdx}`,
      `overwr_io_val_ana_add_overwr_io_${actualIdx}`,
      `overwr_io_timeout_add_overwr_io_${actualIdx}`,
      `overwr_io_add_overwr_io_${actualIdx}`
    ];
    for (let key of keysToDelete) {
      delete parent.temp_settings[overwr_io_sid][key];
    }
  };

  const delFunctionOverwrIO = (actualIdx) => {
    let vv = _.cloneDeep(values.overwr_io);
    if (!vv.oios[actualIdx]) vv.oios[actualIdx] = {};
    vv.oios[actualIdx].__deleted__ = true;
    vv.functions_used_cnt = vv.oios.filter((o) => o && !o.__deleted__).length;
    setValues((prev) => ({ ...prev, overwr_io: vv }));
    // Use deletion sentinel so restoreTempSettings can recognize removal
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_function`, undefined);
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_func_index`, undefined);
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_select`, undefined);
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_val_ana`, undefined);
    controls.overwr_io.saveTempSettings(`overwr_io_${actualIdx}_timeout`, undefined);
  };

  function getAvailableOverwrIOTypes(actualIdx) {
    // Determine which functions are fully consumed (non‑indexable used once OR indexable all indices used)
    const excludedFuncs = getSelectedFuncs(
      typeof actualIdx === 'number' ? actualIdx : -1 // -1 => treat all wizard rows (no current row)
    );

    // Build a reverse map: hwType -> at least one non‑excluded function
    const hwTypes = ['digital_out', 'analog_out'];
    const available = [];

    for (const hwType of hwTypes) {
      const labels = multiMultiIOHwTypeMappingSelect[hwType] || [];
      // Find any function key whose label is in labels and NOT excluded
      const hasFreeFunc = Object.entries(multiIOMapping)
        .filter(([funcKey, label]) => {
          if (funcKey === 'default' || funcKey === '19') return false;
          return labels.includes(label);
        })
        .some(([funcKey]) => !excludedFuncs.includes(Number(funcKey)));

      if (hasFreeFunc) {
        available.push(hwType);
      }
    }
    return available;
  }

  function getSelectedFuncs(actualIdx) {
    const prefix = 'overwr_io_func_add_overwr_io_';
    const temp = parent.temp_settings[overwr_io_sid] ?? {};

    // Collect function + (optional) index usages from other temp wizard entries AND existing entries
    const usage = {};

    // Existing persisted overwrite IO entries (values.overwr_io.oios)
    Object.values(values.overwr_io.oios ?? {})
      .filter((o) => o && !o.__deleted__)
      .forEach((o) => {
        const func = o.function;
        if (func === undefined) return;
        if (funcIndexConfig[func]) {
          const funcIndex = o.func_index;
          if (!usage[func]) usage[func] = new Set();
          usage[func].add(funcIndex);
        } else {
          usage[func] = true;
        }
      });

    // Temp wizard additions (excluding current row)
    Object.keys(temp)
      .filter((k) => k.startsWith(prefix) && k !== `${prefix}${actualIdx}`)
      .forEach((k) => {
        const wizardIdx = k.replace(prefix, '');
        const func = temp[k];
        if (func === undefined) return;

        if (funcIndexConfig[func]) {
          const idxKey = `overwr_io_func_index_add_overwr_io_${wizardIdx}`;
          const funcIndex = temp[idxKey];
          if (!usage[func]) usage[func] = new Set();
          usage[func].add(funcIndex);
        } else {
          usage[func] = true;
        }
      });

    // Build exclusion list:
    // - Non-indexable: exclude immediately if seen
    // - Indexable: exclude only if all possible indices are used
    return Object.entries(usage)
      .filter(([func, used]) => {
        if (used === true) return true;
        const cfg = funcIndexConfig[func];
        const total =
          (cfg && multiIOCounters[cfg.countKey]) != null
            ? multiIOCounters[cfg.countKey]
            : 0;
        if (total <= 0) return false;
        return used.size >= total;
      })
      .map(([func]) => Number(func));
  }

  function getSelectedFuncsIndexes(actualIdx) {
    // Collect already chosen indices for the SAME function in other temp wizard entries and existing persisted overwrite IO entries. Prevent duplicate index selection.
    const funcPrefix = 'overwr_io_func_add_overwr_io_';
    const idxPrefix = 'overwr_io_func_index_add_overwr_io_';
    const settings = parent.temp_settings[overwr_io_sid] ?? {};

    // Current function at this wizard position
    const currentFunc = settings[`${funcPrefix}${actualIdx}`];
    if (currentFunc === undefined) return [];

    // Only indexable functions (present in funcIndexConfig) require exclusion logic
    if (!funcIndexConfig[currentFunc]) return [];

    const used = new Set();

    // Indices from other temp wizard entries (same function)
    Object.keys(settings)
      .filter((k) => k.startsWith(funcPrefix) && k !== `${funcPrefix}${actualIdx}`)
      .forEach((k) => {
        const otherIdx = k.replace(funcPrefix, '');
        const func = settings[k];
        if (func !== currentFunc) return;
        const indexKey = `${idxPrefix}${otherIdx}`;
        const funcIndex = settings[indexKey];
        used.add(funcIndex);
      });

    // Indices from existing persisted overwrite IO entries (same function)
    Object.values(values.overwr_io.oios ?? {})
      .filter((o) => o && !o.__deleted__)
      .forEach((o) => {
        if (o.function === currentFunc && funcIndexConfig[currentFunc]) {
          used.add(o.func_index);
        }
      });

    return [...used];
  }
  // End Overwr IO Handlers

  // Multi IO Handlers
  const saveIOOverwrSelect = (slave, port_id, type, event, value) => {
    const v = Number(event.target.value);
    let vv = _.cloneDeep(values.multi_io);
    vv[slave][port_id].overwr_select = v;
    setValues((prev) => ({ ...prev, multi_io: vv }));
    controls.multi_io.saveTempSettings(`multi_out_${slave}_${port_id}_overwr_select`, v);
  };

  const saveIOOverwrValAna = (slave, port_id, event, value) => {
    let v = event.target.value;
    let vv = _.cloneDeep(values.multi_io);
    vv[slave][port_id].overwr_val_ana = v;
    setValues((prev) => ({ ...prev, multi_io: vv }));
    controls.multi_io.saveTempSettings(`multi_out_${slave}_${port_id}_overwr_val_ana`, v);
  };

  const saveIOOverwrTimeout = (slave, port_id, event, value) => {
    let vv = _.cloneDeep(values.multi_io);
    vv[slave][port_id].overwr_timeout = value.toLocaleTimeString('de-DE');
    setValues((prev) => ({ ...prev, multi_io: vv }));
    controls.multi_io.saveTempSettings(
      `multi_out_${slave}_${port_id}_overwr_timeout`,
      value.toLocaleTimeString('de-DE')
    );
  };

  const saveIOAddOverwrIOSelectType = (slave, actualIdx, event, value) => {
    let v = event.target.value;
    controls.multi_io.saveTempSettings(`multi_out_${slave}_type_add_overwr_io_${actualIdx}`, v);
  };

  const saveIOAddOverwrIOSelectIO = (slave, actualIdx, event, value) => {
    let v = event.target.value;
    controls.multi_io.saveTempSettings(`multi_out_${slave}_id_add_overwr_io_${actualIdx}`, v);
  };

  const saveIOAddOverwrIOSelect = (slave, actualIdx, type, event, value) => {
    const v = Number(event.target.value);
    controls.multi_io.saveTempSettings(`multi_out_${slave}_select_add_overwr_io_${actualIdx}`, v);
  };

  const saveIOAddOverwrIOValAna = (slave, actualIdx, event, value) => {
    let v = event.target.value;
    controls.multi_io.saveTempSettings(`multi_out_${slave}_val_ana_add_overwr_io_${actualIdx}`, v);
  };

  const saveIOAddOverwrIOTimeout = (slave, actualIdx, event, value) => {
    controls.multi_io.saveTempSettings(
      `multi_out_${slave}_timeout_add_overwr_io_${actualIdx}`,
      value.toLocaleTimeString('de-DE')
    );
  };

  const addIOOverwrIO = (slave) => {
    const prefix = `multi_out_${slave}_add_overwr_io_`;
    const indices = Object.keys(parent.temp_settings[multi_io_sid] ?? {})
      .filter((k) => k.startsWith(prefix))
      .map((k) => parseInt(k.replace(prefix, ''), 10));
    const nextIndex = indices.length > 0 ? Math.max(...indices) + 1 : 0;
    controls.multi_io.saveTempSettings(`${prefix}${nextIndex}`, 1);
  };

  function canIOAddOverwrIO(slave) {
    // At least one hardware type still has a free (not fully consumed) function
    return getAvailableMultiIOTypes(slave).length > 0;
  }

  const delIOAddOverwrIO = (slave, actualIdx) => {
    const keysToDelete = [
      `multi_out_${slave}_type_add_overwr_io_${actualIdx}`,
      `multi_out_${slave}_id_add_overwr_io_${actualIdx}`,
      `multi_out_${slave}_select_add_overwr_io_${actualIdx}`,
      `multi_out_${slave}_val_ana_add_overwr_io_${actualIdx}`,
      `multi_out_${slave}_timeout_add_overwr_io_${actualIdx}`,
      `multi_out_${slave}_add_overwr_io_${actualIdx}`
    ];
    for (let key of keysToDelete) {
      delete parent.temp_settings[multi_io_sid][key];
    }
  };

  const delIOOverwrIO = (slave, port_id) => {
    let vv = _.cloneDeep(values.multi_io);
    vv[slave][port_id] = {
      ...vv[slave][port_id],
      overwr_select: 0,
      overwr_val_ana: 0,
      overwr_timeout: '00:00:00'
    };
    setValues((prev) => ({ ...prev, multi_io: vv }));
    controls.multi_io.saveTempSettings(`multi_out_${slave}_${port_id}_overwr_deleted`, true);
    controls.multi_io.saveTempSettings(`multi_out_${slave}_${port_id}_overwr_select`, 0);
    controls.multi_io.saveTempSettings(`multi_out_${slave}_${port_id}_overwr_val_ana`, 0);
    controls.multi_io.saveTempSettings(`multi_out_${slave}_${port_id}_overwr_timeout`, '00:00:00');
  };

  function getHwTypeFromFunc(func) {
    const key = multiIOMapping[func] ?? multiIOMapping['default'];
    for (const [hwType, keys] of Object.entries(multiMultiIOHwTypeMappingSelect)) {
      if (keys.includes(key)) {
        return hwType;
      }
    }
    return 'undefined';
  }

  function getAvailableMultiIOTypes(slave, currentActualIdx = null) {
    const selectedIds = Object.keys(parent.temp_settings[multi_io_sid] ?? {})
      .filter((k) => {
        if (!k.startsWith(`multi_out_${slave}_id_add_overwr_io_`)) return false;
        // Exclude current wizard row if provided
        if (currentActualIdx !== null) {
          const idx = k.replace(`multi_out_${slave}_id_add_overwr_io_`, '');
          if (parseInt(idx, 10) === currentActualIdx) return false;
        }
        return true;
      })
      .map((k) => parent.temp_settings[multi_io_sid][k]);

    const types = ['digital_out', 'analog_out'];
    return types.filter((type) =>
      Object.values(values.multi_io[slave] ?? {}).some(
        (v) =>
          !ioRowListed(slave, v) &&
          !selectedIds.includes(v.id) &&
          multiIOHwTypeMappping[v.type_id] === type
      )
    );
  }

  function getSelectedIdsForSlave(slave, actualIdx) {
    const prefix = `multi_out_${slave}_id_add_overwr_io_`;
    return Object.keys(parent.temp_settings[multi_io_sid] ?? {})
      .filter(
        (k) => k.startsWith(prefix) && k !== `${prefix}${actualIdx}` // Exclude current index
      )
      .map((k) => parent.temp_settings[multi_io_sid][k]);
  }

  function getPortObjProp(values, parent, slave, actualIdx, prop) {
    const selectedId =
      parent.temp_settings[multi_io_sid][`multi_out_${slave}_id_add_overwr_io_${actualIdx}`] ?? '';
    const portObj = values.multi_io[slave][selectedId];
    return portObj[prop] ?? '';
  }
  // End Multi IO Handlers

  // Check if temp settings exists
  function tempSettingsExist(sid, key) {
    return parent.temp_settings[sid][key] !== undefined;
  }

  return (
    <React.Fragment>
      <LoadSettingsDialog
        open={openOverwrIOLoadDialog}
        handleClose={closeOverwrIOLoadDialog}
        controls={controls.overwr_io}
      />

      <LoadSettingsDialog
        open={openMultiIOLoadDialog}
        handleClose={closeMultiIOLoadDialog}
        controls={controls.multi_io}
      />

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={() => {
            controls.overwr_io.resetSettings();
            controls.multi_io.resetSettings();
          }}
          onSave={saveOverrides}
        />
      )}

      {/* Overwrite Function */}
      <ExpandableSettingsGroup
        expand_id={'overwr_io_overwrite_by_function_hl'}
        label={t('overwrite_by_function_hl')}
        expand_on_load={parent.temp_settings[overwr_io_sid]}
      >
        <div
          className={classes.box}
          style={{ padding: '1em', width: '100%', marginBottom: '0.5em' }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {Object.entries(values.overwr_io.oios)
              .filter(([, o]) => o && !o.__deleted__)
              .sort(([, a], [, b]) => {
                const typePriority = (o) => {
                  const hw = getHwTypeFromFunc(o.function);
                  return hw === 'digital_out' ? 0 : 1; // digital_out first
                };
                const pa = typePriority(a);
                const pb = typePriority(b);
                if (pa !== pb) return pa - pb;

                // NEW: sort within same hw type by configured order lists from constants (multiMultiIOHwTypeMappingSelect)
                // Simply remove this block to revert to sorting according to multiIOMapping
                const labelA = multiIOMapping[a.function];
                const labelB = multiIOMapping[b.function];
                const digitalOrder = multiMultiIOHwTypeMappingSelect.digital_out;
                const analogOrder = multiMultiIOHwTypeMappingSelect.analog_out;

                const hwA = getHwTypeFromFunc(a.function);
                const hwB = getHwTypeFromFunc(b.function);

                if (hwA === hwB && (hwA === 'digital_out' || hwA === 'analog_out')) {
                  const orderArr = hwA === 'digital_out' ? digitalOrder : analogOrder;
                  const ia = orderArr.indexOf(labelA);
                  const ib = orderArr.indexOf(labelB);
                  if (ia !== -1 || ib !== -1) {
                    // Only apply if at least one found
                    if (ia === -1) return 1;
                    if (ib === -1) return -1;
                    if (ia !== ib) return ia - ib;
                  }
                }
                // End NEW

                const toNum = (f) => (f === undefined || f === '' ? Infinity : Number(f));
                const fa = toNum(a.function);
                const fb = toNum(b.function);
                if (fa !== fb) return fa - fb;

                if (funcIndexConfig[fa]) {
                  const ia = Number(a.func_index);
                  const ib = Number(b.func_index);
                  if (ia !== ib) return ia - ib;
                }
                return 0;
              })
              .map(([actualIdx, v]) => (
                <Grid
                  key={`${v.zone}:${v.function}:${v.func_index}`}
                  container
                  spacing={1}
                  style={{ marginBottom: '0.3em' }}
                >
                  <Grid item xs={12}>
                    <ValueBox>
                      <div
                        style={
                          mobile
                            ? { alignItems: 'center', gap: '1em' }
                            : { display: 'flex', alignItems: 'center', gap: '1em' }
                        }
                      >
                        <div>
                          <OverwrIOHeading
                            parent={parent}
                            controller={controller}
                            func={v.function ?? ''}
                          />
                          <OverwrIOFuncSelect
                            parent={parent}
                            controller={controller}
                            value={v.function ?? ''}
                            onChange={(event, value) =>
                              saveFunctionOverwrFuncSelect(actualIdx, event, value)
                            }
                            disabled={true}
                          />
                        </div>
                        {funcIndexConfig[v.function] && (
                          <MultiIOSelectIndex
                            parent={parent}
                            controller={controller}
                            count={
                              multiIOCounters[funcIndexConfig[v.function].countKey] ??
                              -1
                            }
                            value={v.func_index ?? ''}
                            onChange={(event, value) =>
                              saveFunctionOverwrFuncIndexSelect(actualIdx, event, value)
                            }
                            disabled={true}
                            {...(funcIndexConfig[v.function].mapping && {
                              mapping: funcIndexConfig[v.function].mapping
                            })}
                          />
                        )}
                        {['digital_out', 'analog_out'].includes(getHwTypeFromFunc(v.function)) && (
                          <OverwrIOSelect
                            parent={parent}
                            controller={controller}
                            select={v.select ?? ''}
                            type={getHwTypeFromFunc(v.function)}
                            onChange={(event, value) =>
                              saveFunctionOverwrSelect(
                                actualIdx,
                                getHwTypeFromFunc(v.function),
                                event,
                                value
                              )
                            }
                            disabled={!write_access}
                          />
                        )}
                        {getHwTypeFromFunc(v.function) === 'analog_out' && (
                          <AnalogOverwriteSetting
                            id={`overwr_io_val_ana_${actualIdx}`}
                            show={'overwr_io_val_ana'}
                            controller={controller}
                            cur_value={v.val_ana ?? ''}
                            controller_value={functionReadback(v)?.val_ana ?? ''}
                            controls={{
                              ...controls.overwr_io,
                              setValue: (id, value) =>
                                saveFunctionOverwrValAna(actualIdx, { target: { value } }, value)
                            }}
                            disabled={!write_access}
                            valueBoxStyle={{
                              boxShadow: 'none',
                              padding: '6px',
                              margin: '0px',
                              maxWidth: '225px'
                            }}
                          />
                        )}
                        <OverwrIOTimeout
                          parent={parent}
                          controller={controller}
                          timeout={v.timeout ?? ''}
                          onChange={(event, value) =>
                            saveFunctionOverwrTimeout(actualIdx, event, value)
                          }
                          disabled={!write_access}
                        />
                        <br />
                        <Button
                          color="primary"
                          onClick={() => delFunctionOverwrIO(actualIdx)}
                          disabled={!write_access}
                        >
                          <DeleteForeverIcon />
                        </Button>
                      </div>
                    </ValueBox>
                  </Grid>
                </Grid>
              ))}
            {/* Add Overwrite Function */}
            {Object.keys(parent.temp_settings[overwr_io_sid] ?? {})
              .filter((k) => k.startsWith('overwr_io_add_overwr_io_'))
              .sort((a, b) => {
                const prefix = 'overwr_io_add_overwr_io_';
                const numA = parseInt(a.replace(prefix, ''), 10);
                const numB = parseInt(b.replace(prefix, ''), 10);
                return numA - numB;
              })
              .map((k) => {
                const prefix = 'overwr_io_add_overwr_io_';
                const actualIdx = parseInt(k.replace(prefix, ''), 10);
                return (
                  <Grid item xs={12} key={k}>
                    <ValueBox>
                      <div
                        style={
                          mobile
                            ? { alignItems: 'center', gap: '1em' }
                            : { display: 'flex', alignItems: 'center', gap: '1em' }
                        }
                      >
                        <div>
                          <OverwrIOSelectType
                            parent={parent}
                            controller={controller}
                            value={
                              parent.temp_settings[overwr_io_sid][
                                `overwr_io_type_add_overwr_io_${actualIdx}`
                              ] ?? 'type'
                            }
                            availableTypes={getAvailableOverwrIOTypes(actualIdx)}
                            onChange={(event, value) =>
                              saveFunctionAddOverwrIOSelectType(actualIdx, event, value)
                            }
                            disabled={!write_access}
                          />
                          <br />
                          {tempSettingsExist(
                            overwr_io_sid,
                            `overwr_io_type_add_overwr_io_${actualIdx}`
                          ) && (
                            <OverwrIOFuncSelect
                              parent={parent}
                              controller={controller}
                              value={
                                parent.temp_settings[overwr_io_sid][
                                  `overwr_io_func_add_overwr_io_${actualIdx}`
                                ] ?? 'overwrite_by_function_hl'
                              }
                              filterByType={
                                parent.temp_settings[overwr_io_sid][
                                  `overwr_io_type_add_overwr_io_${actualIdx}`
                                ]
                              }
                              selectedFuncs={getSelectedFuncs(actualIdx)}
                              onChange={(event, value) =>
                                saveFunctionAddOverwrIOFuncSelect(actualIdx, event, value)
                              }
                              disabled={!write_access}
                            />
                          )}
                        </div>
                        {funcIndexConfig[
                          parent.temp_settings[overwr_io_sid][
                            `overwr_io_func_add_overwr_io_${actualIdx}`
                          ]
                        ] && (
                          <MultiIOSelectIndex
                            parent={parent}
                            controller={controller}
                            count={
                              multiIOCounters[
                                funcIndexConfig[
                                  parent.temp_settings[overwr_io_sid][
                                    `overwr_io_func_add_overwr_io_${actualIdx}`
                                  ]
                                ].countKey
                              ] ?? -1
                            }
                            value={
                              parent.temp_settings[overwr_io_sid][
                                `overwr_io_func_index_add_overwr_io_${actualIdx}`
                              ] ?? 'io_index'
                            }
                            selectedFuncsIndexes={getSelectedFuncsIndexes(actualIdx)}
                            onChange={(event, value) =>
                              saveFunctionAddOverwrIOFuncIndexSelect(actualIdx, event, value)
                            }
                            disabled={!write_access}
                            {...(funcIndexConfig[
                              parent.temp_settings[overwr_io_sid][
                                `overwr_io_func_add_overwr_io_${actualIdx}`
                              ]
                            ].mapping && {
                              mapping:
                                funcIndexConfig[
                                  parent.temp_settings[overwr_io_sid][
                                    `overwr_io_func_add_overwr_io_${actualIdx}`
                                  ]
                                ].mapping
                            })}
                          />
                        )}
                        {funcIndexConfig[
                          parent.temp_settings[overwr_io_sid][
                            `overwr_io_func_add_overwr_io_${actualIdx}`
                          ]
                        ] &&
                          tempSettingsExist(
                            overwr_io_sid,
                            `overwr_io_func_index_add_overwr_io_${actualIdx}`
                          ) && (
                            <>
                              {['digital_out', 'analog_out'].includes(
                                getHwTypeFromFunc(
                                  parent.temp_settings[overwr_io_sid][
                                    `overwr_io_func_add_overwr_io_${actualIdx}`
                                  ]
                                )
                              ) && (
                                <OverwrIOSelect
                                  parent={parent}
                                  controller={controller}
                                  select={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_select_add_overwr_io_${actualIdx}`
                                    ] ?? getNewOverwriteMode(
                                      parent.temp_settings[overwr_io_sid][
                                        `overwr_io_type_add_overwr_io_${actualIdx}`
                                      ]
                                    )
                                  }
                                  type={getHwTypeFromFunc(
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_func_add_overwr_io_${actualIdx}`
                                    ]
                                  )}
                                  onChange={(event, value) =>
                                    saveFunctionAddOverwrIOSelect(
                                      actualIdx,
                                      getHwTypeFromFunc(
                                        parent.temp_settings[overwr_io_sid][
                                          `overwr_io_func_add_overwr_io_${actualIdx}`
                                        ]
                                      ),
                                      event,
                                      value
                                    )
                                  }
                                  disabled={!write_access}
                                />
                              )}
                              {getHwTypeFromFunc(
                                parent.temp_settings[overwr_io_sid][
                                  `overwr_io_func_add_overwr_io_${actualIdx}`
                                ]
                              ) === 'analog_out' && (
                                <AnalogOverwriteSetting
                                  id={`overwr_io_val_ana_${actualIdx}`}
                                  show={'overwr_io_val_ana'}
                                  controller={controller}
                                  cur_value={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_val_ana_add_overwr_io_${actualIdx}`
                                    ] ?? 0
                                  }
                                  controller_value={null}
                                  controls={{
                                    ...controls.overwr_io,
                                    setValue: (id, value) =>
                                      saveFunctionAddOverwrIOValAna(
                                        actualIdx,
                                        { target: { value } },
                                        value
                                      )
                                  }}
                                  disabled={!write_access}
                                  valueBoxStyle={{
                                    boxShadow: 'none',
                                    padding: '6px',
                                    margin: '0px',
                                    maxWidth: '225px'
                                  }}
                                />
                              )}
                              {tempSettingsExist(
                                overwr_io_sid,
                                `overwr_io_val_ana_add_overwr_io_${actualIdx}`
                              ) && (
                                <OverwrIOTimeout
                                  parent={parent}
                                  controller={controller}
                                  timeout={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_timeout_add_overwr_io_${actualIdx}`
                                    ] ?? '00:00:00'
                                  }
                                  onChange={(event, value) =>
                                    saveFunctionAddOverwrIOTimeout(actualIdx, event, value)
                                  }
                                  disabled={!write_access}
                                />
                              )}
                              {getHwTypeFromFunc(
                                parent.temp_settings[overwr_io_sid][
                                  `overwr_io_func_add_overwr_io_${actualIdx}`
                                ]
                              ) === 'digital_out' && (
                                <OverwrIOTimeout
                                  parent={parent}
                                  controller={controller}
                                  timeout={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_timeout_add_overwr_io_${actualIdx}`
                                    ] ?? '00:00:00'
                                  }
                                  onChange={(event, value) =>
                                    saveFunctionAddOverwrIOTimeout(actualIdx, event, value)
                                  }
                                  disabled={!write_access}
                                />
                              )}
                            </>
                          )}
                        {!funcIndexConfig[
                          parent.temp_settings[overwr_io_sid][
                            `overwr_io_func_add_overwr_io_${actualIdx}`
                          ]
                        ] &&
                          tempSettingsExist(
                            overwr_io_sid,
                            `overwr_io_func_add_overwr_io_${actualIdx}`
                          ) && (
                            <>
                              {['digital_out', 'analog_out'].includes(
                                getHwTypeFromFunc(
                                  parent.temp_settings[overwr_io_sid][
                                    `overwr_io_func_add_overwr_io_${actualIdx}`
                                  ]
                                )
                              ) && (
                                <OverwrIOSelect
                                  parent={parent}
                                  controller={controller}
                                  select={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_select_add_overwr_io_${actualIdx}`
                                    ] ?? getNewOverwriteMode(
                                      parent.temp_settings[overwr_io_sid][
                                        `overwr_io_type_add_overwr_io_${actualIdx}`
                                      ]
                                    )
                                  }
                                  type={getHwTypeFromFunc(
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_func_add_overwr_io_${actualIdx}`
                                    ]
                                  )}
                                  onChange={(event, value) =>
                                    saveFunctionAddOverwrIOSelect(
                                      actualIdx,
                                      getHwTypeFromFunc(
                                        parent.temp_settings[overwr_io_sid][
                                          `overwr_io_func_add_overwr_io_${actualIdx}`
                                        ]
                                      ),
                                      event,
                                      value
                                    )
                                  }
                                  disabled={!write_access}
                                />
                              )}
                              {getHwTypeFromFunc(
                                parent.temp_settings[overwr_io_sid][
                                  `overwr_io_func_add_overwr_io_${actualIdx}`
                                ]
                              ) === 'analog_out' && (
                                <AnalogOverwriteSetting
                                  id={`overwr_io_val_ana_${actualIdx}`}
                                  show={'overwr_io_val_ana'}
                                  controller={controller}
                                  cur_value={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_val_ana_add_overwr_io_${actualIdx}`
                                    ] ?? 0
                                  }
                                  controller_value={null}
                                  controls={{
                                    ...controls.overwr_io,
                                    setValue: (id, value) =>
                                      saveFunctionAddOverwrIOValAna(
                                        actualIdx,
                                        { target: { value } },
                                        value
                                      )
                                  }}
                                  disabled={!write_access}
                                  valueBoxStyle={{
                                    boxShadow: 'none',
                                    padding: '6px',
                                    margin: '0px',
                                    maxWidth: '225px'
                                  }}
                                />
                              )}
                              {tempSettingsExist(
                                overwr_io_sid,
                                `overwr_io_val_ana_add_overwr_io_${actualIdx}`
                              ) && (
                                <OverwrIOTimeout
                                  parent={parent}
                                  controller={controller}
                                  timeout={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_timeout_add_overwr_io_${actualIdx}`
                                    ] ?? '00:00:00'
                                  }
                                  onChange={(event, value) =>
                                    saveFunctionAddOverwrIOTimeout(actualIdx, event, value)
                                  }
                                  disabled={!write_access}
                                />
                              )}
                              {getHwTypeFromFunc(
                                parent.temp_settings[overwr_io_sid][
                                  `overwr_io_func_add_overwr_io_${actualIdx}`
                                ]
                              ) === 'digital_out' && (
                                <OverwrIOTimeout
                                  parent={parent}
                                  controller={controller}
                                  timeout={
                                    parent.temp_settings[overwr_io_sid][
                                      `overwr_io_timeout_add_overwr_io_${actualIdx}`
                                    ] ?? '00:00:00'
                                  }
                                  onChange={(event, value) =>
                                    saveFunctionAddOverwrIOTimeout(actualIdx, event, value)
                                  }
                                  disabled={!write_access}
                                />
                              )}
                            </>
                          )}
                        <br />
                        <Button
                          color="primary"
                          onClick={() => delFunctionAddOverwrIO(actualIdx)}
                          disabled={!write_access}
                        >
                          <DeleteForeverIcon />
                        </Button>
                      </div>
                    </ValueBox>
                  </Grid>
                );
              })}
            {/* End Add Overwrite Function */}
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Button
                  className={classes.addButton}
                  color="primary"
                  onClick={addFunctionOverwrIO}
                  disabled={
                    !write_access ||
                    !canFunctionAddOverwrIO() ||
                    Object.keys(parent.temp_settings[overwr_io_sid] ?? {})
                      .filter((k) => k.startsWith('overwr_io_add_overwr_io_'))
                      .some((k) => {
                        const prefix = 'overwr_io_add_overwr_io_';
                        const actualIdx = parseInt(k.replace(prefix, ''), 10);
                        const typeKey = `overwr_io_type_add_overwr_io_${actualIdx}`;
                        const funcKey = `overwr_io_func_add_overwr_io_${actualIdx}`;
                        const needsIndex =
                          funcIndexConfig[parent.temp_settings[overwr_io_sid][funcKey]] !==
                          undefined;
                        const indexKey = `overwr_io_func_index_add_overwr_io_${actualIdx}`;
                        // Require type + function (+ index if indexable) before allowing new row
                        return !(
                          tempSettingsExist(overwr_io_sid, typeKey) &&
                          tempSettingsExist(overwr_io_sid, funcKey) &&
                          (!needsIndex || tempSettingsExist(overwr_io_sid, indexKey))
                        );
                      })
                  }
                >
                  {t('add_overwr_function')}
                </Button>
              </Grid>
            </Grid>
          </div>
        </div>
      </ExpandableSettingsGroup>
      {/* End Overwrite Function */}

      {/* Overwrite I/O */}
      <ExpandableSettingsGroup
        expand_id={'overwr_io_overwrite_by_io_hl'}
        label={t('overwrite_by_io_hl')}
        expand_on_load={parent.temp_settings[multi_io_sid]}
      >
        {Object.keys(values.multi_io).map((slave) => (
          <div
            key={slave}
            className={classes.box}
            style={{ padding: '1em', width: '100%', marginBottom: '0.5em' }}
          >
            <span style={{ fontSize: '1.5em', display: 'flex', alignItems: 'center' }}>
              {' '}
              {t('slave_address_hl')}{' '}
              <div style={{ fontFamily: 'monospace', marginLeft: '0.5em' }}>{slave.toString()}</div>
              <ConnectionStatus
                controllerStatus={controller?.connection?.status}
                slave={Object.values(slaves).filter((s) => s.address == slave)[0]}
              />
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Grid container spacing={1}>
                {Object.values(values.multi_io[slave]).map(
                  (v) =>
                    v.type_id !== 0 &&
                    ioRowListed(slave, v) && (
                      <Grid key={v.id} item xs={12}>
                        <ValueBox>
                          <div
                            style={
                              mobile
                                ? { alignItems: 'center', gap: '1em' }
                                : { display: 'flex', alignItems: 'center', gap: '1em' }
                            }
                          >
                            <OverwrMultiIOHeading
                              controller={controller}
                              parent={parent}
                              index={getPhysicalIOLabel(controllerSnapshot, slave, v.id)}
                              memo={v.memo ?? ''}
                              type={v.type_id ?? ''}
                            />
                            <MultiIOSelect
                              parent={parent}
                              controller={controller}
                              value={v.func ?? ''}
                              type={v.type_id ?? ''}
                              disabled={true}
                            />
                            {funcIndexConfig[v.func] && (
                              <MultiIOSelectIndex
                                count={
                                  multiIOCounters[funcIndexConfig[v.func].countKey] ??
                                  -1
                                }
                                value={v.func_index ?? ''}
                                disabled={true}
                                {...(funcIndexConfig[v.func].mapping && {
                                  mapping: funcIndexConfig[v.func].mapping
                                })}
                              />
                            )}
                            {['digital_out', 'analog_out'].includes(
                              multiIOHwTypeMappping[v.type_id]
                            ) && (
                              <OverwrIOSelect
                                parent={parent}
                                controller={controller}
                                select={v.overwr_select ?? ''}
                                type={v.type_id ?? ''}
                                onChange={(event, value) =>
                                  saveIOOverwrSelect(slave, v.id, v.type_id ?? '', event, value)
                                }
                                disabled={!write_access}
                              />
                            )}
                            {multiIOHwTypeMappping[v.type_id] == 'analog_out' && (
                              <AnalogOverwriteSetting
                                id={`multi_out_${slave}_${v.id}_overwr_val_ana`}
                                show={'overwr_io_val_ana'}
                                controller={controller}
                                cur_value={v.overwr_val_ana ?? ''}
                                controller_value={
                                  multi_outs?.[slave]?.[v.id]?.overwr_val_ana ?? ''
                                }
                                controls={{
                                  ...controls.multi_io,
                                  setValue: (id, value) =>
                                    saveIOOverwrValAna(slave, v.id, { target: { value } }, value)
                                }}
                                disabled={!write_access}
                                valueBoxStyle={{
                                  boxShadow: 'none',
                                  padding: '6px',
                                  margin: '0px',
                                  maxWidth: '225px'
                                }}
                              />
                            )}
                            <OverwrIOTimeout
                              parent={parent}
                              controller={controller}
                              timeout={v.overwr_timeout ?? ''}
                              onChange={(event, value) =>
                                saveIOOverwrTimeout(slave, v.id, event, value)
                              }
                              disabled={!write_access}
                            />
                            <br />
                            <Button
                              color="primary"
                              aria-label={`Delete IO override ${slave}:${v.id}`}
                              onClick={() => delIOOverwrIO(slave, v.id)}
                              disabled={!write_access}
                            >
                              <DeleteForeverIcon />
                            </Button>
                          </div>
                        </ValueBox>
                      </Grid>
                    )
                )}
                {/* Add Overwrite I/O */}
                {Object.keys(parent.temp_settings[multi_io_sid] ?? {})
                  .filter((k) => k.startsWith(`multi_out_${slave}_add_overwr_io_`))
                  .sort((a, b) => {
                    const prefix = `multi_out_${slave}_add_overwr_io_`;
                    const numA = parseInt(a.replace(prefix, ''), 10);
                    const numB = parseInt(b.replace(prefix, ''), 10);
                    return numA - numB;
                  })
                  .map((k) => {
                    const prefix = `multi_out_${slave}_add_overwr_io_`;
                    const actualIdx = parseInt(k.replace(prefix, ''), 10);
                    return (
                      <Grid item xs={12} key={k}>
                        <ValueBox>
                          <div
                            style={
                              mobile
                                ? { alignItems: 'center', gap: '1em' }
                                : { display: 'flex', alignItems: 'center', gap: '1em' }
                            }
                          >
                            <div>
                              <OverwrIOSelectType
                                parent={parent}
                                controller={controller}
                                value={
                                  parent.temp_settings[multi_io_sid][
                                    `multi_out_${slave}_type_add_overwr_io_${actualIdx}`
                                  ] ?? 'type'
                                }
                                availableTypes={getAvailableMultiIOTypes(slave, actualIdx)}
                                onChange={(event, value) =>
                                  saveIOAddOverwrIOSelectType(slave, actualIdx, event, value)
                                }
                                disabled={!write_access}
                              />
                              <br />
                              {tempSettingsExist(
                                multi_io_sid,
                                `multi_out_${slave}_type_add_overwr_io_${actualIdx}`
                              ) && (
                                <OverwrIOSelectIO
                                  parent={parent}
                                  controller={controllerSnapshot}
                                  type={
                                    parent.temp_settings[multi_io_sid][
                                      `multi_out_${slave}_type_add_overwr_io_${actualIdx}`
                                    ]
                                  }
                                  multiIO={values.multi_io[slave]}
                                  slaveAddress={slave}
                                  value={
                                    parent.temp_settings[multi_io_sid][
                                      `multi_out_${slave}_id_add_overwr_io_${actualIdx}`
                                    ] ?? 'multifunctional_io'
                                  }
                                  selectedIds={[
                                    ...getSelectedIdsForSlave(slave, actualIdx),
                                    ...Object.values(values.multi_io[slave] ?? {})
                                      .filter((row) => ioRowListed(slave, row))
                                      .map((row) => row.id)
                                  ]}
                                  onChange={(event, value) =>
                                    saveIOAddOverwrIOSelectIO(slave, actualIdx, event, value)
                                  }
                                  disabled={!write_access}
                                />
                              )}
                              {tempSettingsExist(
                                multi_io_sid,
                                `multi_out_${slave}_id_add_overwr_io_${actualIdx}`
                              ) &&
                                (() => {
                                  const memo = getPortObjProp(
                                    values,
                                    parent,
                                    slave,
                                    actualIdx,
                                    'memo'
                                  );
                                  return memo !== -1 && memo !== '' ? (
                                    <>
                                      <Typography gutterBottom></Typography>
                                      {'(' + memo + ')'}
                                    </>
                                  ) : null;
                                })()}
                            </div>
                            {tempSettingsExist(
                              multi_io_sid,
                              `multi_out_${slave}_id_add_overwr_io_${actualIdx}`
                            ) && (
                              <>
                                <MultiIOSelect
                                  parent={parent}
                                  controller={controller}
                                  value={
                                    getPortObjProp(values, parent, slave, actualIdx, 'func') ?? ''
                                  }
                                  type={
                                    getPortObjProp(values, parent, slave, actualIdx, 'type_id') ??
                                    ''
                                  }
                                  disabled={true}
                                />
                                {funcIndexConfig[
                                  getPortObjProp(values, parent, slave, actualIdx, 'func')
                                ] && (
                                  <MultiIOSelectIndex
                                    count={
                                      multiIOCounters[
                                        funcIndexConfig[
                                          getPortObjProp(values, parent, slave, actualIdx, 'func')
                                        ].countKey
                                      ] ?? -1
                                    }
                                    value={
                                      getPortObjProp(
                                        values,
                                        parent,
                                        slave,
                                        actualIdx,
                                        'func_index'
                                      ) ?? ''
                                    }
                                    disabled={true}
                                    {...(funcIndexConfig[
                                      getPortObjProp(values, parent, slave, actualIdx, 'func')
                                    ].mapping && {
                                      mapping:
                                        funcIndexConfig[
                                          getPortObjProp(values, parent, slave, actualIdx, 'func')
                                        ].mapping
                                    })}
                                  />
                                )}
                                {['digital_out', 'analog_out'].includes(
                                  multiIOHwTypeMappping[
                                    getPortObjProp(values, parent, slave, actualIdx, 'type_id')
                                  ]
                                ) && (
                                  <OverwrIOSelect
                                    parent={parent}
                                    controller={controller}
                                    select={
                                      parent.temp_settings[multi_io_sid][
                                        `multi_out_${slave}_select_add_overwr_io_${actualIdx}`
                                      ] ?? getNewOverwriteMode(
                                        getPortObjProp(values, parent, slave, actualIdx, 'type_id')
                                      )
                                    }
                                    type={
                                      getPortObjProp(values, parent, slave, actualIdx, 'type_id') ??
                                      ''
                                    }
                                    onChange={(event, value) =>
                                      saveIOAddOverwrIOSelect(
                                        slave,
                                        actualIdx,
                                        getPortObjProp(values, parent, slave, actualIdx, 'type_id'),
                                        event,
                                        value
                                      )
                                    }
                                    disabled={!write_access}
                                  />
                                )}
                                {multiIOHwTypeMappping[
                                  getPortObjProp(values, parent, slave, actualIdx, 'type_id')
                                ] === 'analog_out' && (
                                  <>
                                    <AnalogOverwriteSetting
                                      id={`multi_out_${slave}_${actualIdx}_overwr_val_ana`}
                                      show={'overwr_io_val_ana'}
                                      controller={controller}
                                      cur_value={
                                        parent.temp_settings[multi_io_sid][
                                          `multi_out_${slave}_val_ana_add_overwr_io_${actualIdx}`
                                        ] ?? 0
                                      }
                                      controller_value={null}
                                      controls={{
                                        ...controls.multi_io,
                                        setValue: (id, value) =>
                                          saveIOAddOverwrIOValAna(
                                            slave,
                                            actualIdx,
                                            { target: { value } },
                                            value
                                          )
                                      }}
                                      disabled={!write_access}
                                      valueBoxStyle={{
                                        boxShadow: 'none',
                                        padding: '6px',
                                        margin: '0px',
                                        maxWidth: '225px'
                                      }}
                                    />
                                    {tempSettingsExist(
                                      multi_io_sid,
                                      `multi_out_${slave}_val_ana_add_overwr_io_${actualIdx}`
                                    ) && (
                                      <OverwrIOTimeout
                                        parent={parent}
                                        controller={controller}
                                        timeout={
                                          parent.temp_settings[multi_io_sid][
                                            `multi_out_${slave}_timeout_add_overwr_io_${actualIdx}`
                                          ] ?? '00:00:00'
                                        }
                                        onChange={(event, value) =>
                                          saveIOAddOverwrIOTimeout(slave, actualIdx, event, value)
                                        }
                                        disabled={!write_access}
                                      />
                                    )}
                                  </>
                                )}
                                {multiIOHwTypeMappping[
                                  getPortObjProp(values, parent, slave, actualIdx, 'type_id')
                                ] === 'digital_out' && (
                                  <OverwrIOTimeout
                                    parent={parent}
                                    controller={controller}
                                    timeout={
                                      parent.temp_settings[multi_io_sid][
                                        `multi_out_${slave}_timeout_add_overwr_io_${actualIdx}`
                                      ] ?? '00:00:00'
                                    }
                                    onChange={(event, value) =>
                                      saveIOAddOverwrIOTimeout(slave, actualIdx, event, value)
                                    }
                                    disabled={!write_access}
                                  />
                                )}
                              </>
                            )}
                            <br />
                            <Button
                              color="primary"
                              onClick={() => delIOAddOverwrIO(slave, actualIdx)}
                              disabled={!write_access}
                            >
                              <DeleteForeverIcon />
                            </Button>
                          </div>
                        </ValueBox>
                      </Grid>
                    );
                  })}
                {/* End Add Overwrite I/O */}
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Button
                      className={classes.addButton}
                      color="primary"
                      onClick={() => addIOOverwrIO(slave)}
                      disabled={
                        !write_access ||
                        !canIOAddOverwrIO(slave) ||
                        Object.keys(parent.temp_settings[multi_io_sid] ?? {})
                          .filter((k) => k.startsWith(`multi_out_${slave}_add_overwr_io_`))
                          .some((k) => {
                            const actualIdx = parseInt(
                              k.replace(`multi_out_${slave}_add_overwr_io_`, ''),
                              10
                            );
                            const typeKey = `multi_out_${slave}_type_add_overwr_io_${actualIdx}`;
                            const idKey = `multi_out_${slave}_id_add_overwr_io_${actualIdx}`;
                            return !(
                              tempSettingsExist(multi_io_sid, typeKey) &&
                              tempSettingsExist(multi_io_sid, idKey)
                            );
                          })
                      }
                    >
                      {t('add_overwr_io')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </div>
        ))}
        {Object.keys(values.multi_io).length === 0 && (
          <div
            className={classes.box}
            style={{ padding: '1em', width: '100%', marginBottom: '0.5em' }}
          >
            <Grid item xs={12}>
              <Button className={classes.addButton} color="primary" onClick={null} disabled={true}>
                {t('add_overwr_io')}
              </Button>
            </Grid>
          </div>
        )}
      </ExpandableSettingsGroup>
      {/* End Overwrite I/O */}

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={() => {
            controls.overwr_io.resetSettings();
            controls.multi_io.resetSettings();
          }}
          onSave={saveOverrides}
        />
      )}
    </React.Fragment>
  );
}
